import { useEffect, useState } from "react";
import { getWebApiBase } from "../../lib/webApi";
import { sanitizeReturnTo } from "../../../shared/safeReturnPath.js";
import { AppleBrandMark, GoogleBrandMark } from "./oauthBrandIcons";
import { useTranslation } from "../../lib/i18n";
import {
  AUTH_OAUTH_SLOT_CLASS,
  AUTH_OAUTH_SLOT_TESTID,
} from "../../lib/wwwPhoneClipLayout";

const API_BASE = getWebApiBase();

/** Apple HIG: light chip + dark mark on dark UI so the button is not charcoal-on-charcoal. */
export const AUTH_OAUTH_APPLE_BUTTON_CLASS =
  "btn-chip flex-1 w-full gap-2 dark:!bg-[var(--text-primary)] dark:!text-[var(--bg-card)] dark:hover:!opacity-90";

/** Outline so the Google chip does not vanish into `--bg-card` when fill is near-white. */
export const AUTH_OAUTH_GOOGLE_BUTTON_CLASS =
  "btn-chip flex-1 w-full gap-2 ring-2 ring-[var(--border-input)]";

function oauthStartUrl(provider, returnTo) {
  const q = new URLSearchParams({ returnTo: sanitizeReturnTo(returnTo) });
  const prefix = API_BASE || "";
  return `${prefix}/api/auth/oauth/${provider}/start?${q.toString()}`;
}

export default function OAuthSocialButtons({ returnTo = "/dashboard", disabled = false }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE || ""}/api/auth/oauth/config`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (!cancelled) setConfig({});
          return;
        }
        const data = await res.json();
        if (!cancelled) setConfig(data);
      } catch {
        if (!cancelled) setConfig({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const googleOn = config?.google?.enabled;
  const appleOn = config?.apple?.enabled;
  const resolved = config !== null;

  if (resolved && !googleOn && !appleOn) return null;

  return (
    <div
      data-testid={AUTH_OAUTH_SLOT_TESTID}
      className={AUTH_OAUTH_SLOT_CLASS}
      role="group"
      aria-label={resolved ? t('auth.oauth.continueWith') : undefined}
      aria-busy={!resolved}
    >
      {resolved ? (
        <>
      <div className="relative flex items-center justify-center py-1">
        <span
          className="absolute inset-x-0 h-px bg-stone-200 dark:bg-stone-700"
          aria-hidden
        />
        <span className="relative bg-[var(--bg-card)] px-3 text-xs text-[var(--text-muted)]">
          {t("auth.oauth.continueWith")}
        </span>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
        {googleOn ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              window.location.href = oauthStartUrl("google", returnTo);
            }}
            className={AUTH_OAUTH_GOOGLE_BUTTON_CLASS}
          >
            <GoogleBrandMark size={20} />
            {t("auth.oauth.google")}
          </button>
        ) : null}
        {appleOn ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              window.location.href = oauthStartUrl("apple", returnTo);
            }}
            className={AUTH_OAUTH_APPLE_BUTTON_CLASS}
          >
            <AppleBrandMark size={18} className="shrink-0" />
            {t("auth.oauth.apple")}
          </button>
        ) : null}
      </div>
        </>
      ) : null}
    </div>
  );
}
